import type { RefObject } from "react";

import {
  PLACEHOLDERS,
  placeholderToken,
  type PlaceholderKey,
} from "../announcement-placeholders";

/**
 * Buttons that insert `{first_name}` and the rest at the cursor of the field
 * above them (TNG-95), replacing any selection, then put the cursor after the
 * insert so typing carries on. The field keeps its cursor position while the
 * button is pressed, so this works with a tap on a phone as well as a click.
 *
 * Nothing is inserted if it would take the field past `maxLength`: the input
 * would accept the value, since the limit only stops typing, and the backend
 * would then refuse the command.
 */
export function PlaceholderChips({
  target,
  value,
  onChange,
  maxLength,
  disabled,
  testId,
}: {
  target: RefObject<HTMLInputElement | HTMLTextAreaElement | null>;
  value: string;
  onChange: (next: string) => void;
  maxLength: number;
  disabled?: boolean;
  testId: string;
}) {
  function insert(key: PlaceholderKey) {
    const element = target.current;
    const token = placeholderToken(key);
    const start = element?.selectionStart ?? value.length;
    const end = element?.selectionEnd ?? start;
    const next = value.slice(0, start) + token + value.slice(end);
    if (next.length > maxLength) return;
    onChange(next);
    const cursor = start + token.length;
    // After React has written the new value, or the cursor lands at the end.
    requestAnimationFrame(() => {
      element?.focus();
      element?.setSelectionRange(cursor, cursor);
    });
  }

  return (
    <div
      className="flex flex-wrap items-center gap-1.5"
      role="group"
      aria-label="Insert a personal detail"
      data-testid={testId}
    >
      <span className="text-xs text-muted-foreground">Insert:</span>
      {PLACEHOLDERS.map((spec) => (
        <button
          key={spec.key}
          type="button"
          disabled={disabled}
          // Keep the field focused so its selection is still there to read.
          onMouseDown={(event) => event.preventDefault()}
          onClick={() => insert(spec.key)}
          aria-label={`Insert ${spec.label.toLowerCase()} placeholder ${placeholderToken(spec.key)}`}
          data-testid={`${testId}-${spec.key}`}
          className="min-h-11 rounded-full border border-input px-3 text-xs font-medium text-foreground transition-colors hover:bg-accent hover:text-accent-foreground focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50 sm:min-h-8"
        >
          {spec.label}
        </button>
      ))}
    </div>
  );
}
